import React, { ReactElement, useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import Backdrop from '@mui/material/Backdrop';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { styled, alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { LogIn } from 'lucide-react';
import { useAuthentication } from '../context/AuthenticationProvider';

const BackgroundImage = `${process.env.PUBLIC_URL}/bgimg.jpg`;

const Root = styled('div')(({ theme }) => ({
  background: `url(${BackgroundImage}) no-repeat fixed center center`,
  backgroundSize: 'cover',
  height: '100vh',
  width: '100%',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    width: '100%',
    background: alpha(theme.palette.grey[300], 0.75),
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  backgroundColor: alpha(theme.palette.background.paper, 0.4),
  boxShadow: 'none',
  padding: theme.spacing(8),
  maxWidth: 700,
  borderRadius: 25,
  [theme.breakpoints.down('md')]: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
  },
}));

export const LoginFragment = (): ReactElement => {
  const { t } = useTranslation('common');
  const { authenticated, inProgress, signinRedirect, refresh } = useAuthentication();
  const [refreshInterval, setRefreshInterval] = useState<
    ReturnType<typeof setInterval> | undefined
  >();
  const state = window.location;

  useEffect(() => {
    if (refreshInterval) {
      return;
    }

    if (authenticated || inProgress) {
      setRefreshInterval(undefined);
      clearInterval(refreshInterval);
    }

    const interval = setInterval(async () => {
      const user = await refresh();
      if (user) {
        setRefreshInterval((prevInterval) => {
          if (prevInterval) {
            clearInterval(prevInterval);
          }

          return undefined;
        });
      }
    }, 500);

    setRefreshInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshInterval, authenticated, inProgress]);

  return (
    <Root>
      {authenticated && <Redirect to={state.pathname} />}
      {inProgress && (
        <Backdrop open={inProgress}>
          <CircularProgress />
        </Backdrop>
      )}
      <StyledCard>
        <CardContent>
          <Typography variant="h2" gutterBottom textAlign="center" sx={{ marginBottom: 4 }}>
            {t('authentication.login.title', 'Welcome!')}
          </Typography>
          <Typography gutterBottom textAlign="center" sx={{ marginBottom: 4 }}>
            {t(
              'authentication.login.text',
              'Please log on to start working with the NDR Redispatch Admin UI.',
            )}
          </Typography>
          <div style={{ textAlign: 'center' }}>
            <Button
              color="primary"
              variant="contained"
              size="large"
              sx={{ fontWeight: 'bold' }}
              startIcon={<LogIn />}
              onClick={() => signinRedirect && signinRedirect(state)}
            >
              {t('authentication.login.logon', 'Log on')}
            </Button>
          </div>
        </CardContent>
      </StyledCard>
    </Root>
  );
};

export default LoginFragment;
